import React, { useRef, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowCircleUp, faArrowCircleDown, faCaretDown, faCaretRight, faChevronRight, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import './dropdown-fix.css';
import './enhanced-dropdown.css';

/**
 * A custom dropdown component with multiple selection and accessibility features
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the dropdown is open
 * @param {Function} props.toggleDropdown - Function to toggle dropdown
 * @param {string} props.displayText - Text to display in the dropdown header
 * @param {Array} props.options - Array of options to display in the dropdown
 * @param {Function} props.isSelected - Function to check if an option is selected (option) => boolean
 * @param {Function} props.onSelect - Function called when an option is selected (option) => void
 * @param {Object} props.themeStyles - Object containing theme-specific styles
 * @param {boolean} props.isDarkMode - Whether the application is in dark mode
 * @param {string} props.label - ARIA label for the dropdown
 * @param {number} props.uiScale - Scale factor for UI sizing (default 1)
 */
const MultiSelectDropdown = ({
  isOpen,
  toggleDropdown,
  displayText,
  options,
  isSelected,
  onSelect,
  themeStyles,
  isDarkMode,
  label = "Select options",
  uiScale = 1
}) => {    const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const headerRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const [dropdownWidth, setDropdownWidth] = useState('auto');
  const [isInSimulateExam, setIsInSimulateExam] = useState(false);
  const [isInPracticeTest, setIsInPracticeTest] = useState(false);
  // State to track expanded parent items
  const [expandedItems, setExpandedItems] = useState({});

  // UI scaling calculations
  const s = uiScale ?? 1;
  const baseFontSize = Math.round(14 * s);
  const basePaddingY = Math.round(10 * s);
  const basePaddingX = Math.round(12 * s);
  const baseIconSize = Math.round(14 * s);
  const baseBorderRadius = Math.round(5 * s);
  const baseItemHeight = Math.round(46 * s);
  const baseItemPaddingY = Math.round(12 * s);
  const baseItemPaddingX = Math.round(15 * s);
  const baseMinHeight = Math.round(22 * s);  // Update the hook so that it can initialize expandedItems with items from an array of objects
  useEffect(() => {
    if (isOpen && Array.isArray(options)) {
      // Initialize expanded state with parent items
      // Only initialize items that don't already have a state
      setExpandedItems(prevExpandedItems => {
        const expandedState = {...prevExpandedItems};
        
        // Helper function to recursively find parent items
        const findParentItems = (items) => {
          if (!Array.isArray(items)) return;
          
          items.forEach(item => {
            const hasChildren = typeof item === 'object' && item !== null && 
                              'children' in item && Array.isArray(item.children) && 
                              item.children.length > 0;
              if (hasChildren) {
              const key = typeof item === 'object' && item !== null && 'id' in item ? item.id : item;
              // Only set default state if this item hasn't been toggled before
              if (expandedState[key] === undefined) {
                expandedState[key] = false; // Default to collapsed
              }
              findParentItems(item.children);
            }
          });
        };
        
        findParentItems(options);
        
        // Only return a new object if changes were made
        return Object.keys(expandedState).length !== Object.keys(prevExpandedItems).length 
          ? expandedState 
          : prevExpandedItems;
      });
    }
  }, [isOpen, options]);
  // Calculate the width needed for the longest item
  const calculateOptimalWidth = () => {
    if (!isOpen || !options || options.length === 0 || !dropdownRef.current) return;

    // Create a temporary hidden div to measure text width more accurately
    const tempDiv = document.createElement('div');
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    // Set fontFamily from the dropdown itself, assuming it's consistent
    tempDiv.style.fontFamily = window.getComputedStyle(dropdownRef.current).fontFamily;

    // Add the checkbox width plus some padding
    const checkboxWidth = 35; // width of checkbox + margin
    
    // Measure each option's width
    document.body.appendChild(tempDiv);
    let maxWidth = 0;
    
    // Handle both simple string options and complex object options
    const measureOptionText = (opt) => {
      const text = typeof opt === 'object' && opt !== null && 'name' in opt ? opt.name : opt;
      const hasChildren = typeof opt === 'object' && opt !== null && 'children' in opt && Array.isArray(opt.children) && opt.children.length > 0;
      
      // Apply specific font styles of items for accurate measurement
      tempDiv.style.fontSize = '14px';
      tempDiv.style.fontWeight = hasChildren ? '600' : '400';
      tempDiv.textContent = text;
      
      const width = tempDiv.getBoundingClientRect().width;
      maxWidth = Math.max(maxWidth, width);
      
      // Recursively measure children if present
      if (hasChildren) {
        opt.children.forEach(child => measureOptionText(child));
      }
    };
    
    options.forEach(opt => measureOptionText(opt));
    
    // Calculate the width of exactly 3 characters for padding
    tempDiv.textContent = 'WWW'; // Using W as it's typically a wide character
    const threeCharWidth = tempDiv.getBoundingClientRect().width;
    
    document.body.removeChild(tempDiv);
    
    // Get the current dropdown button width as minimum width
    const headerWidth = headerRef.current ? headerRef.current.offsetWidth : 0;
    
    // Total width needed is: text width + checkbox width + padding (exactly 3 characters width)
    const optimalWidth = Math.max(headerWidth, maxWidth + checkboxWidth + threeCharWidth);
    
    setDropdownWidth(optimalWidth);
    
    // Position the menu properly based on the header's position
    if (menuRef.current && headerRef.current) {
      const headerRect = headerRef.current.getBoundingClientRect();
      if (dropdownPosition === 'bottom') {
        menuRef.current.style.top = `${headerRect.bottom + 5}px`;
        menuRef.current.style.left = `${headerRect.left}px`;
      } else {
        menuRef.current.style.bottom = `${window.innerHeight - headerRect.top + 5}px`;
        menuRef.current.style.left = `${headerRect.left}px`;
      }
    }
  };// Check for available space and position dropdown accordingly
  useEffect(() => {
    if (isOpen && dropdownRef.current && menuRef.current && headerRef.current) {
      const headerRect = headerRef.current.getBoundingClientRect();      const dropdownItemHeight = baseItemHeight; // Approximate height of each item with padding
      const visibleItemsLimit = 5; // Limit to show only 5 items at once
      const maxVisibleHeight = dropdownItemHeight * visibleItemsLimit; // Height for 5 items
      const menuHeight = options.length <= visibleItemsLimit ? 
                         options.length * dropdownItemHeight : 
                         maxVisibleHeight; // Calculate exact height based on number of items
      const spaceBelow = window.innerHeight - headerRect.bottom;
      
      // Fix for SimulateExam component - force position directly under header
      // Update the state variables for component context
      const inSimulateExam = document.querySelector('.simulate-exam') && dropdownRef.current.closest('.simulate-exam');
      const inPracticeTest = document.querySelector('.practice-test') && dropdownRef.current.closest('.practice-test');
      setIsInSimulateExam(inSimulateExam);
      setIsInPracticeTest(inPracticeTest);
      
      if (inSimulateExam || inPracticeTest) {
        setDropdownPosition('bottom');
        // Set this specifically for SimulateExam or PracticeTest
        menuRef.current.style.position = 'absolute';
        menuRef.current.style.top = `${headerRect.bottom}px`;
        menuRef.current.style.left = `${headerRect.left}px`;
      }
      
      // If there's not enough space below, position above if there's more space there
      else if (spaceBelow < menuHeight && headerRect.top > menuHeight) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
      
      // Calculate optimal width when dropdown opens
      calculateOptimalWidth();
      
      // After width is calculated and menu is open, check if it extends off-screen
      setTimeout(() => {
        if (menuRef.current) {
          const menuRect = menuRef.current.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          
          // If menu extends beyond right edge of viewport, adjust position
          if (menuRect.right > viewportWidth) {
            // Calculate how much to shift left to stay in viewport
            const overflowAmount = menuRect.right - viewportWidth;
            const newLeft = Math.max(0, headerRect.left - overflowAmount - 10); // 10px buffer
            menuRef.current.style.left = `${newLeft}px`;
          }
        }
      }, 0);
    }
  }, [isOpen, options]);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (isOpen && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        toggleDropdown();
      }
    }
    
    // Handle scrolling to reposition the dropdown if needed
    function handleScroll() {
      if (isOpen && menuRef.current && headerRef.current) {
        const headerRect = headerRef.current.getBoundingClientRect();
        
        // Update position based on current header position
        if (dropdownPosition === 'bottom') {
          menuRef.current.style.top = `${headerRect.bottom + 5}px`;
          menuRef.current.style.left = `${headerRect.left}px`;
        } else {
          menuRef.current.style.bottom = `${window.innerHeight - headerRect.top + 5}px`;
          menuRef.current.style.left = `${headerRect.left}px`;
        }
      }
    }
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen, toggleDropdown, dropdownPosition]);

  // Prevent body scrolling when dropdown is open (but only for touchscreen devices)
  useEffect(() => {
    if (isOpen) {
      // Store original scroll position
      const scrollY = window.scrollY;
      
      // Function to prevent default touch move to stop scrolling while ensuring dropdown still works
      const preventScroll = (e) => {
        // Don't prevent scrolling inside the dropdown itself
        if (menuRef.current && menuRef.current.contains(e.target)) {
          return;
        }
        window.scrollTo(0, scrollY);
      };
      
      // Only add for touch devices
      if ('ontouchstart' in window) {
        window.addEventListener('scroll', preventScroll, { passive: false });
      }
      
      return () => {
        if ('ontouchstart' in window) {
          window.removeEventListener('scroll', preventScroll);
        }
      };
    }
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      // Toggle dropdown on Enter or Space
      e.preventDefault();
      toggleDropdown();
    } else if (e.key === 'Escape' && isOpen) {
      // Close dropdown on Escape
      e.preventDefault();
      toggleDropdown();
    } else if (e.key === 'ArrowDown' && isOpen) {
      // Focus the first item when pressing down arrow
      e.preventDefault();
      const firstItem = dropdownRef.current.querySelector('.dropdown-item');
      if (firstItem) firstItem.focus();
    }
  };

  // Handle keyboard navigation within dropdown items
  const handleItemKeyDown = (e, option, index) => {
    if (e.key === 'Enter' || e.key === ' ') {
      // Select item on Enter or Space
      e.preventDefault();
      onSelect(option);
    } else if (e.key === 'ArrowDown') {
      // Move focus to next item
      e.preventDefault();
      const nextItem = e.currentTarget.nextElementSibling;
      if (nextItem) nextItem.focus();
    } else if (e.key === 'ArrowUp') {
      // Move focus to previous item
      e.preventDefault();
      const prevItem = e.currentTarget.previousElementSibling;
      if (prevItem) {
        prevItem.focus();
      } else {
        // If at first item, go back to dropdown header
        dropdownRef.current.querySelector('.dropdown-header').focus();
      }
    } else if (e.key === 'Home') {
      // Move to first item
      e.preventDefault();
      dropdownRef.current.querySelector('.dropdown-item').focus();
    } else if (e.key === 'End') {
      // Move to last item
      e.preventDefault();
      const items = dropdownRef.current.querySelectorAll('.dropdown-item');
      items[items.length - 1].focus();
    } else if (e.key === 'Escape') {
      // Close dropdown and return focus to header
      e.preventDefault();
      toggleDropdown();
      dropdownRef.current.querySelector('.dropdown-header').focus();
    }
  };

  return (
    <div 
      className="custom-dropdown" 
      ref={dropdownRef} 
      style={{ 
        position: 'relative',
        '--dd-menu-pad-y': `${Math.round(5 * s)}px`,
        '--dd-menu-pad-x': '0px',
        '--dd-checkbox-size': `${Math.round(22 * s)}px`,
        '--dd-checkbox-radius': `${Math.round(3 * s)}px`,
        '--dd-checkbox-margin': `${Math.round(12 * s)}px`,
        '--dd-hierarchy-left': `${Math.round(12 * s)}px`,
        '--dd-hierarchy-width': `${Math.round(1 * s)}px`,
        '--dd-branch-width': `${Math.round(8 * s)}px`,
        '--dd-branch-height': `${Math.round(1 * s)}px`
      }}
    >
      {/* Dropdown trigger button */}      
      <div 
        className="dropdown-header"
        ref={headerRef}
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        tabIndex="0"
        role="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={label}
        style={{
          background: themeStyles.select.background,
          color: themeStyles.select.color,
          border: themeStyles.select.border,
          borderRadius: `${baseBorderRadius}px`,
          padding: `${basePaddingY}px ${basePaddingX}px`,
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'relative',
          boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)',
          transition: 'all 0.2s ease',
          outline: 'none',
          width: '200px' // Ensure the header takes full width of parent
        }}
      >
        <span style={{ marginRight: `${Math.round(5 * s)}px` }}>
          {displayText}
        </span>
        <FontAwesomeIcon 
          icon={isOpen ? faArrowCircleUp : faArrowCircleDown} 
          style={{ fontSize: `${baseIconSize}px` }}
        />
      </div>
      
      {/* Dropdown menu */}      
      {isOpen && (
        <div          className="dropdown-menu"
          ref={menuRef}
          role="listbox"
          aria-multiselectable="true"          style={{
            // Position conditionally based on whether we're in SimulateExam/PracticeTest or not
            position: (isInSimulateExam || isInPracticeTest) ? 'absolute' : 'fixed',
            ...(dropdownPosition === 'bottom' 
              ? { top: headerRef.current ? headerRef.current.getBoundingClientRect().bottom + 5 : 0 }
              : { bottom: window.innerHeight - (headerRef.current ? headerRef.current.getBoundingClientRect().top : 0) + 5 }),
            left: headerRef.current ? headerRef.current.getBoundingClientRect().left : 0,            zIndex: 9999, // Ensure highest z-index
            minWidth: headerRef.current ? headerRef.current.offsetWidth : '100%',
            width: dropdownWidth === 'auto' ? 'auto' : `${dropdownWidth}px`,
            maxWidth: `min(${dropdownWidth === 'auto' ? 300 : dropdownWidth}px, calc(100vw - 40px))`, // Ensure it doesn't go off screen
            background: isDarkMode ? '#1c2740' : '#fff',
            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid #dcdfe6',
            borderRadius: `${baseBorderRadius}px`,
            maxHeight: options.length <= 5 ? 'auto' : `${baseItemHeight * 5}px`, // Show exactly 5 items or less if fewer options
            overflowY: options.length <= 5 ? 'visible' : 'auto', // Only add scrolling if more than 5 items
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}
        >          {/* Recursively render options */}          {(() => {
            // Add debugging to see what options are being passed to the component
            // console.log('[MultiSelectDropdown] rendering with options:', options);
            
            // Helper function to recursively render options and their children
            const renderOptions = (options, depth = 0) => {
              if (!Array.isArray(options)) {
                // console.log('[MultiSelectDropdown] options is not an array:', options);
                return null;
              }
              
              // console.log(`[MultiSelectDropdown] rendering ${options.length} options at depth ${depth}`);
              
              return options.flatMap((option, index) => {                // Handle complex objects with name property
                const optionText = typeof option === 'object' && option !== null ? 
                  (option.label || option.name || option.toString()) : 
                  option;
                // console.log(`[MultiSelectDropdown] option ${index}:`, option, 'rendered as:', optionText);
                const optionKey = typeof option === 'object' && option !== null ? 
                  (option.id || option.key || optionText) : 
                  option;
                const hasChildren = typeof option === 'object' && option !== null && 'children' in option && Array.isArray(option.children) && option.children.length > 0;
                const isExpanded = hasChildren && expandedItems[optionKey];
                  // Handler for clicking an option
                const handleSelect = (e) => {
                  // If user clicked directly on the expansion toggle, don't select the option
                  if (e.target.classList.contains('expansion-toggle')) {
                    return;
                  }
                  
                  // Let the parent component handle the selection logic
                  // Pass the original option object so wrapper functions have all the data
                  onSelect(option);
                };
                
                // Create the option element
                const result = [                  <div 
                    key={`${optionKey}-${depth}-${index}`}
                    className={`dropdown-item ${hasChildren ? 'parent-item' : 'child-item'}`}                    onClick={handleSelect}
                    onKeyDown={(e) => handleItemKeyDown(e, option, index)}
                    role="option"
                    aria-selected={isSelected(option)}
                    aria-expanded={hasChildren ? isExpanded : undefined}
                    tabIndex="0"
                    style={{
                      padding: `${baseItemPaddingY}px ${baseItemPaddingX}px`,
                      paddingLeft: `${baseItemPaddingX + (depth * Math.round(20 * s))}px`, // Indent based on depth
                      cursor: 'pointer',
                      borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)',
                      display: 'flex',
                      alignItems: 'center',
                      transition: 'background 0.2s',                      background: isSelected(option) 
                        ? (isDarkMode ? 'rgba(61, 139, 255, 0.18)' : 'rgba(25, 118, 210, 0.1)')
                        : 'transparent',
                      fontSize: `${baseFontSize}px`,
                      fontWeight: hasChildren ? '600' : '400', // Bold parent categories
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      color: hasChildren ? (isDarkMode ? '#e2e8f0' : '#2d3748') : themeStyles.text.primary,
                      outline: 'none',
                      minHeight: `${baseMinHeight}px`, // Ensure consistent height for calculation
                      boxSizing: 'border-box', // Include padding in height calculation
                      position: 'relative' // Added for hierarchy lines
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = isDarkMode 
                        ? 'rgba(255, 255, 255, 0.1)' 
                        : 'rgba(0, 0, 0, 0.03)';
                    }}                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = isSelected(option)
                        ? (isDarkMode ? 'rgba(61, 139, 255, 0.18)' : 'rgba(25, 118, 210, 0.1)')
                        : 'transparent';
                    }}
                  >
                    {/* Add visual hierarchy lines for child items */}
                    {depth > 0 && !hasChildren && (
                      <div className="hierarchy-line" style={{
                        left: `${7 + ((depth - 1) * 20)}px`
                      }}></div>
                    )}
                    {depth > 0 && !hasChildren && (
                      <div className="hierarchy-branch" style={{
                        left: `${7 + ((depth - 1) * 20)}px`
                      }}></div>
                    )}
                    {/* Add expand/collapse indicator for parent items */}
                    {hasChildren && (
                      <span 
                        className={`expansion-toggle ${isExpanded ? 'expanded' : ''}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation(); // Prevent the click from selecting the option
                          // Directly update expanded state for immediate response
                          setExpandedItems(prev => ({
                            ...prev,
                            [optionKey]: !prev[optionKey]
                          }));
                        }}
                        style={{ 
                          cursor: 'pointer', 
                          marginRight: '8px',
                          display: 'inline-flex',
                          width: '24px',
                          height: '24px',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                          borderRadius: '3px',
                          transition: 'all 0.15s ease',
                          zIndex: 5 // Higher z-index to ensure click priority
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = isDarkMode 
                            ? 'rgba(255, 255, 255, 0.15)' 
                            : 'rgba(0, 0, 0, 0.08)';
                          e.stopPropagation();
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = isDarkMode 
                            ? 'rgba(255, 255, 255, 0.08)' 
                            : 'rgba(0, 0, 0, 0.04)';
                          e.stopPropagation();
                        }}
                      >
                        <FontAwesomeIcon 
                          icon={isExpanded ? faChevronDown : faChevronRight} 
                          style={{ 
                            fontSize: '12px',
                            color: isDarkMode ? '#a0aec0' : '#666',
                            pointerEvents: 'none', // Prevent icon from capturing events
                            transition: 'transform 0.2s ease'
                          }}
                        />
                      </span>
                    )}
                      {/* If not a parent, add spacing for alignment */}
                    {!hasChildren && (
                      <span style={{ width: '24px', marginRight: '8px' }}></span>
                    )}                    <div style={{
                      width: '22px',
                      height: '22px',
                      border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.4)' : '1px solid #ccc',
                      borderRadius: '3px',
                      marginRight: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: isSelected(option) 
                        ? (isDarkMode ? '#0074cc' : '#1976d2') 
                        : 'transparent',
                      flexShrink: 0
                    }}>
                      {isSelected(option) && (
                        <span style={{ color: 'white', fontSize: '13px' }}>✓</span>
                      )}
                    </div>
                    {optionText}
                  </div>
                ];
                
                // Recursively render children if they exist AND the parent is expanded
                if (hasChildren && isExpanded) {
                  result.push(...renderOptions(option.children, depth + 1));
                }
                
                return result;
              });
            };
            
            return renderOptions(options);
          })()}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
